import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Zap, TrendingUp, TrendingDown, CheckCircle, ArrowRight,
  Loader2, Shield, Users, Clock, Sparkles, Mail,
} from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/early`;

export default function EarlyAccessPage() {
  document.title = "ROAS-AI Turbo Plan | Early Bird Access — 50% Off";

  const [step, setStep] = useState("signup"); // signup, verify, done
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [code, setCode] = useState("");
  const [signupId, setSignupId] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    fetch(`${API}/stats`).then(r => r.json()).then(setStats).catch(() => {});
  }, []);

  const handleSignup = async () => {
    if (!form.first_name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(`${API}/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await resp.json();
      if (resp.ok) {
        setSignupId(data.signup_id);
        setStep("verify");
        toast.success("Verification code sent to your email!");
      } else {
        toast.error(data.detail || "Signup failed");
      }
    } catch { toast.error("Connection error"); }
    finally { setLoading(false); }
  };

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error("Enter the 6-digit code from your email");
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(`${API}/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signup_id: signupId, code }),
      });
      const data = await resp.json();
      if (resp.ok) {
        setPosition(data.position);
        setStep("done");
        toast.success("You're in!");
      } else {
        toast.error(data.detail || "Invalid code");
      }
    } catch { toast.error("Connection error"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <div className="border-b border-white/5 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <a href="/login" className="flex items-center gap-2">
            <img src="/roas-ai-logo.png" alt="ROAS-AI" className="w-8 h-8 object-contain" />
            <span className="font-heading text-lg font-bold">ROAS-AI</span>
          </a>
          <a href="/pricing-demo" className="text-zinc-400 hover:text-white text-sm">View Plans</a>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* First Fold: Hero + Stats + Form */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Left: Hero + Stats */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30 mb-3 text-sm px-4 py-1">
              Early Bird — 50% Off for First 100 Clients
            </Badge>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 leading-tight">
              ROAS-AI <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-400 to-red-400">Turbo</span>
            </h1>
            <p className="text-zinc-400 text-base mb-6">
              The next evolution of AI-powered ad optimization. Get notified when it launches.
            </p>

            {/* Key Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#121212] border border-emerald-500/30 rounded-xl p-5 text-center">
                <TrendingUp className="w-7 h-7 text-emerald-400 mx-auto mb-2" />
                <p className="text-3xl font-bold text-emerald-400">45%</p>
                <p className="text-white font-semibold text-sm">Increase in ROAS</p>
                <p className="text-zinc-400 text-xs">Within the first week</p>
              </div>
              <div className="bg-[#121212] border border-violet-500/30 rounded-xl p-5 text-center">
                <TrendingDown className="w-7 h-7 text-violet-400 mx-auto mb-2" />
                <p className="text-3xl font-bold text-violet-400">35%</p>
                <p className="text-white font-semibold text-sm">Reduction in CAC</p>
                <p className="text-zinc-400 text-xs">Within the first week</p>
              </div>
            </div>
          </motion.div>

          {/* Right: Signup / Verify / Done */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
          {step === "signup" && (
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-8">
              <div className="text-center mb-6">
                <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                <h2 className="text-xl font-bold">Get Early Bird Access</h2>
                <p className="text-zinc-400 text-sm mt-1">Be first in line when Turbo launches</p>
              </div>
              <div className="space-y-4">
                <Input
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  placeholder="First Name"
                  className="bg-black/50 border-white/10 text-white h-12 rounded-xl"
                />
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && handleSignup()}
                  placeholder="Email Address"
                  className="bg-black/50 border-white/10 text-white h-12 rounded-xl"
                />
                <Button
                  onClick={handleSignup}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold h-12 rounded-xl text-base"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Get Notified for Early Bird Access
                </Button>
              </div>
            </div>
          )}

          {step === "verify" && (
            <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 text-center">
              <Mail className="w-10 h-10 text-amber-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Verify Your Email</h2>
              <p className="text-zinc-400 text-sm mb-6">We sent a 6-digit code to <span className="text-white">{form.email}</span></p>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => e.key === "Enter" && handleVerify()}
                placeholder="000000"
                maxLength={6}
                className="bg-black/50 border-white/10 text-white h-14 rounded-xl text-center text-2xl font-mono tracking-[0.5em] mb-4"
                autoFocus
              />
              <Button
                onClick={handleVerify}
                disabled={loading || code.length !== 6}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold h-12 rounded-xl"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Verify & Secure My Spot
              </Button>
              <button onClick={() => { setStep("signup"); toast.info("Enter your email again to resend"); }} className="text-zinc-400 text-sm mt-4 hover:text-zinc-300 transition-colors">
                Didn't get the code? Resend
              </button>
            </div>
          )}

          {step === "done" && (
            <div className="bg-[#121212] border-2 border-emerald-500/30 rounded-2xl p-8 text-center">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">You're In!</h2>
              <div className="bg-black/50 rounded-xl p-4 mb-6">
                <p className="text-amber-400 font-bold text-lg">50% Early Bird Discount</p>
                <p className="text-zinc-400 text-sm">Will be applied automatically at launch</p>
              </div>
              <p className="text-zinc-500 text-sm">We'll email you as soon as Turbo is ready.</p>
              <a href="/login" className="inline-flex items-center gap-1 text-violet-400 hover:text-violet-300 text-sm mt-4 transition-colors">
                Explore ROAS-AI now <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          )}
        </motion.div>
        </div>

        {/* Features */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <h2 className="text-2xl font-bold text-center mb-8">What's in Turbo</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {[
              { icon: Zap, title: "AI Auto-Pilot", desc: "Fully autonomous budget optimization across Google, Meta, LinkedIn & Bing — no manual intervention" },
              { icon: TrendingUp, title: "Predictive ROAS", desc: "Machine learning models predict campaign performance 7 days ahead, reallocating budget before drops happen" },
              { icon: Shield, title: "Smart Guardrails", desc: "Set min/max ROAS thresholds and budget floors — Turbo optimizes aggressively within your safety limits" },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="bg-[#121212] border border-white/10 rounded-xl p-6">
                  <Icon className="w-6 h-6 text-amber-400 mb-3" />
                  <h3 className="text-white font-semibold mb-1">{f.title}</h3>
                  <p className="text-zinc-400 text-sm">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Social Proof */}
        <div className="text-center mb-12">
          <p className="text-zinc-500 text-sm">Trusted by 50+ marketing teams worldwide</p>
          <div className="flex items-center justify-center gap-6 mt-3 text-zinc-500 text-xs">
            <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> SOC2 Compliant</span>
            <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3" /> PCI via Stripe</span>
            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> 50+ Teams</span>
          </div>
        </div>

        {/* Footer */}
        <div className="py-8 border-t border-white/5 flex items-center justify-between text-xs text-zinc-500">
          <div className="flex gap-4">
            <a href="/privacy" className="hover:text-zinc-400">Privacy Policy</a>
            <a href="/terms" className="hover:text-zinc-400">Terms of Service</a>
            <a href="/about" className="hover:text-zinc-400">About Us</a>
          </div>
          <span>Copyright (c) 2026 Beruf LLC</span>
        </div>
      </div>
    </div>
  );
}
